import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import {
  checkProspectZips,
  expandProspectServiceAreas,
  searchProspectServiceAreas,
  uploadProspectZipFile,
} from '@src/api/actions/prospects';
import ProjectionZipMap from './ProjectionZipMap';

// ---------------------------------------------------------------------------
// Table helpers — pasted text and uploaded files both become a table of
// cells, then the applicant says which column is which.
// ---------------------------------------------------------------------------
const ROLES = [
  { value: 'zip', label: 'ZIP code' },
  { value: 'territory', label: 'Territory / market' },
  { value: 'vertical', label: 'Vertical' },
  { value: 'brand', label: 'Brand' },
  { value: 'ignore', label: 'Ignore' },
];

const isZipLike = (v) => /^\d{4,5}(-\d{4})?$/.test(String(v || '').trim().replace(/\.0+$/, ''));

export function parseTextTable(text) {
  const lines = (text || '').split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const sample = lines.slice(0, 50).join('\n');
  const delims = ['\t', ',', '|', ';'];
  const counts = delims.map((d) => sample.split(d).length - 1);
  const best = Math.max(...counts);
  if (!best) return lines.map((l) => [l.trim()]);
  const delim = delims[counts.indexOf(best)];
  const rows = lines.map((l) => l.split(delim).map((c) => c.trim().replace(/^"|"$/g, '')));
  // "60601, 60602, 60603 ..." on one or two lines is a list, not columns.
  if (rows.length <= 3 && Math.max(...rows.map((r) => r.length)) > 6) {
    const flat = rows.flat().filter(Boolean);
    if (flat.filter(isZipLike).length >= 0.6 * flat.length) return flat.map((c) => [c]);
  }
  return rows;
}

function hasHeaderRow(table) {
  if (table.length < 2) return false;
  return !table[0].some(isZipLike) && table.slice(1, 6).some((r) => r.some(isZipLike));
}

function guessRoles(table, header) {
  const width = Math.max(0, ...table.map((r) => r.length));
  const body = header ? table.slice(1, 200) : table.slice(0, 200);
  const roles = Array.from({ length: width }, (_, i) => {
    const name = header ? String(table[0][i] || '').toLowerCase() : '';
    if (/zip|postal/.test(name)) return 'zip';
    if (/territor|region|market|branch|area/.test(name)) return 'territory';
    if (/vertical|service|product|trade/.test(name)) return 'vertical';
    if (/brand/.test(name)) return 'brand';
    return 'ignore';
  });
  if (!roles.includes('zip')) {
    // No ZIP header: the column with the most ZIP-looking values.
    const scores = roles.map((_, i) => body.filter((r) => isZipLike(r[i])).length);
    const best = scores.indexOf(Math.max(...scores));
    if (best >= 0 && scores[best] > 0) roles[best] = 'zip';
  }
  return roles;
}

function rowsFromTable(table, roles, header) {
  const body = header ? table.slice(1) : table;
  const idx = (role) => roles.indexOf(role);
  const zi = idx('zip');
  if (zi < 0) return [];
  return body.map((r) => ({
    zip: r[zi] || '',
    territory: idx('territory') >= 0 ? r[idx('territory')] || '' : '',
    vertical: idx('vertical') >= 0 ? r[idx('vertical')] || '' : '',
    brand: idx('brand') >= 0 ? r[idx('brand')] || '' : '',
  }));
}

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const fmtPop = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : `${Math.round((n || 0) / 1000)}k`);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ZipIntakeStep({ value, onChange }) {
  // value = { rows, territories, features, report, helpRequest, source }.
  // onChange takes a partial update and merges it into value.
  const [mode, setMode] = useState(value.source?.mode || 'upload');
  const [table, setTable] = useState(value.source?.table || []);
  const [header, setHeader] = useState(value.source?.header || false);
  const [roles, setRoles] = useState(value.source?.roles || []);
  const [fileName, setFileName] = useState(value.source?.fileName || '');
  const [pasteText, setPasteText] = useState(value.source?.pasteText || '');
  const [areas, setAreas] = useState(value.source?.areas || []); // [{...area, rows}]
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [renames, setRenames] = useState({});
  const fileInput = useRef(null);
  const checkSeq = useRef(0);

  const setTableFrom = (t) => {
    const h = hasHeaderRow(t);
    setTable(t);
    setHeader(h);
    setRoles(guessRoles(t, h));
    setRenames({});
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploadError('');
    setFileName(file.name);
    if (/\.(csv|tsv|txt)$/i.test(file.name)) {
      setTableFrom(parseTextTable(await file.text()));
      return;
    }
    setIsUploading(true);
    try {
      const res = await uploadProspectZipFile(file);
      setTableFrom(res?.data?.rows || []);
    } catch (err) {
      setUploadError(err?.response?.data?.detail || "We couldn't read that file. Try saving it as CSV.");
      setTable([]);
    } finally {
      setIsUploading(false);
    }
  };

  const pasteMounted = useRef(false);
  useEffect(() => {
    // Skip the first run so coming back to this step keeps the column choices.
    if (!pasteMounted.current) { pasteMounted.current = true; return; }
    if (mode === 'paste') setTableFrom(parseTextTable(pasteText));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasteText]);

  const listRows = useMemo(() => rowsFromTable(table, roles, header), [table, roles, header]);
  const areaRows = useMemo(() => areas.flatMap((a) => a.rows || []), [areas]);
  const sourceRows = useMemo(() => [...listRows, ...areaRows], [listRows, areaRows]);

  // Check + group into markets whenever the inputs change.
  useEffect(() => {
    const seq = ++checkSeq.current;
    const source = { mode, table, header, roles, fileName, pasteText, areas };
    if (!sourceRows.length) {
      onChange({ rows: [], territories: [], features: [], report: null, source });
      return undefined;
    }
    setIsChecking(true);
    const t = setTimeout(async () => {
      try {
        const res = await checkProspectZips(sourceRows);
        if (seq !== checkSeq.current) return;
        const d = res?.data || {};
        onChange({ rows: d.rows || [], territories: d.territories || [], features: d.features || [], report: d.report || null, source });
        setRenames({});
      } catch {
        if (seq === checkSeq.current) onChange({ rows: [], territories: [], features: [], report: null, source });
      } finally {
        if (seq === checkSeq.current) setIsChecking(false);
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceRows]);

  const applyRename = (oldName, newName) => {
    setRenames((r) => ({ ...r, [oldName]: newName }));
  };
  const commitRename = (oldName) => {
    const newName = (renames[oldName] || '').trim();
    const taken = value.territories.some((t) => t.name !== oldName && t.name.toLowerCase() === newName.toLowerCase());
    if (!newName || newName === oldName || taken) {
      setRenames((r) => { const n = { ...r }; delete n[oldName]; return n; });
      return;
    }
    onChange({
      rows: value.rows.map((r) => (r.territory === oldName ? { ...r, territory: newName } : r)),
      territories: value.territories.map((t) => (t.name === oldName ? { ...t, name: newName } : t)),
      features: value.features.map((f) => (f.properties?.territory === oldName
        ? { ...f, properties: { ...f.properties, territory: newName } }
        : f)),
    });
    setRenames((r) => { const n = { ...r }; delete n[oldName]; return n; });
  };

  const report = value.report;
  const territories = value.territories || [];
  const zipCount = report?.valid_zip_count || 0;
  const totalPop = territories.reduce((s, t) => s + (t.population || 0), 0);
  const merged = territories.filter((t) => t.submitted_label_count > 1 || t.folded_markets?.length);
  const isolated = territories.filter((t) => t.isolated);
  const hasZipColumn = roles.includes('zip');

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">Your Service Area</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload your ZIP code list, paste it, or pick the counties and cities you serve. We'll show you the map before you continue.
        </Typography>
      </Box>

      <Tabs value={mode} onChange={(_, m) => setMode(m)} variant="fullWidth">
        <Tab value="upload" label="Upload a file" />
        <Tab value="paste" label="Paste a list" />
        <Tab value="areas" label="Pick counties or cities" />
      </Tabs>

      {mode === 'upload' && (
        <Paper
          variant="outlined"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
          sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}
        >
          <input
            ref={fileInput}
            type="file"
            hidden
            accept=".csv,.xlsx,.xlsm,.tsv,.txt"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <UploadFileIcon color="action" sx={{ fontSize: 40 }} />
          <Typography sx={{ mt: 1 }}>Drag your Excel or CSV file here</Typography>
          <Button sx={{ mt: 1 }} variant="outlined" onClick={() => fileInput.current?.click()} disabled={isUploading}>
            {isUploading ? <CircularProgress size={18} /> : 'Choose file'}
          </Button>
          {fileName && <Typography variant="caption" display="block" sx={{ mt: 1 }}>{fileName}</Typography>}
          {uploadError && <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>{uploadError}</Alert>}
        </Paper>
      )}

      {mode === 'paste' && (
        <TextField
          multiline
          minRows={6}
          maxRows={14}
          placeholder={'Paste from Excel or type one ZIP per line:\n80903\n80904\n80905'}
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          fullWidth
        />
      )}

      {mode === 'areas' && <AreaPicker areas={areas} setAreas={setAreas} />}

      {mode !== 'areas' && table.length > 0 && (
        <ColumnMapper table={table} header={header} setHeader={setHeader} roles={roles} setRoles={setRoles} />
      )}
      {mode !== 'areas' && table.length > 0 && !hasZipColumn && (
        <Alert severity="warning">Tell us which column has the ZIP codes.</Alert>
      )}

      {isChecking && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Checking your ZIP codes…</Typography>
        </Stack>
      )}

      {!isChecking && report && (
        <>
          {zipCount > 0 ? (
            <Alert severity="success" variant="outlined">
              <strong>{fmt(zipCount)}</strong> ZIP codes, about <strong>{fmtPop(totalPop)}</strong> people, in{' '}
              <strong>{territories.length}</strong> market{territories.length === 1 ? '' : 's'}.
            </Alert>
          ) : (
            <Alert severity="error">We didn't find any valid ZIP codes. Check the ZIP column, or pick counties and cities instead.</Alert>
          )}
          {(report.invalid_count > 0 || report.duplicate_count > 0 || report.unknown_count > 0) && (
            <Alert severity="info" variant="outlined">
              {report.invalid_count > 0 && (
                <div>Skipped {fmt(report.invalid_count)} entries that aren't ZIP codes (e.g. {report.invalid_samples.slice(0, 3).map((s) => `"${s}"`).join(', ')}).</div>
              )}
              {report.duplicate_count > 0 && <div>Removed {fmt(report.duplicate_count)} duplicates.</div>}
              {report.unknown_count > 0 && (
                <div>{fmt(report.unknown_count)} ZIP codes don't exist in our US ZIP list and were left out ({report.unknown_samples.slice(0, 4).join(', ')}).</div>
              )}
            </Alert>
          )}
        </>
      )}

      {!isChecking && territories.length > 0 && (
        <>
          {merged.length > 0 && (
            <Alert severity="info">
              We set up territories by market, the way we run and report on campaigns. Areas inside the same market are combined:
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {merged.slice(0, 6).map((t) => {
                  const parts = [];
                  if (t.submitted_label_count > 1) {
                    parts.push(`${t.submitted_labels.slice(0, 4).join(', ')}${t.submitted_label_count > 4 ? ` and ${t.submitted_label_count - 4} more` : ''}`);
                  }
                  if (t.folded_markets?.length) parts.push(`nearby ${t.folded_markets.join(', ')}`);
                  return <li key={t.name}><strong>{t.name}</strong>: {parts.join('; ')}</li>;
                })}
              </Box>
            </Alert>
          )}
          {isolated.length > 0 && (
            <Alert severity="warning" variant="outlined">
              {isolated.map((t) => t.name).join(', ')} {isolated.length === 1 ? 'is' : 'are'} small and far from the rest of your area. If that's a mistake, fix those ZIPs; otherwise you're fine.
            </Alert>
          )}
          {totalPop > 0 && totalPop < 1_000_000 && (
            <Alert severity="warning" variant="outlined">
              Your service area covers about {fmtPop(totalPop)} people. Campaigns perform best above 1 million, so consider adding more ZIP codes.
            </Alert>
          )}

          {value.features?.length > 0 && (
            <ProjectionZipMap
              features={value.features}
              territories={territories.map((t) => ({ territory: t.name }))}
              subheader="Each color is one territory."
              showFilterLegend={false}
            />
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>Your territories</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              You can rename a territory. To change what's in it, change your ZIP list.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Territory</TableCell>
                  <TableCell>Market</TableCell>
                  <TableCell align="right">ZIPs</TableCell>
                  <TableCell align="right">Population</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {territories.map((t) => (
                  <TableRow key={t.name}>
                    <TableCell sx={{ minWidth: 180 }}>
                      <TextField
                        size="small"
                        variant="standard"
                        value={renames[t.name] ?? t.name}
                        onChange={(e) => applyRename(t.name, e.target.value)}
                        onBlur={() => commitRename(t.name)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>{t.market}</TableCell>
                    <TableCell align="right">{fmt(t.zip_count)}</TableCell>
                    <TableCell align="right">{fmt(t.population)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}

      {!isChecking && zipCount === 0 && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffaf5' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <HelpOutlineIcon color="warning" fontSize="small" />
            <Typography fontWeight={600}>Having trouble with your ZIP codes?</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Describe the area you serve and our team will build the ZIP list with you. You can still finish the application.
          </Typography>
          <TextField
            multiline
            minRows={2}
            fullWidth
            placeholder="e.g. All of New Jersey plus Bucks County, PA"
            value={value.helpRequest || ''}
            onChange={(e) => onChange({ helpRequest: e.target.value })}
          />
        </Paper>
      )}
    </Stack>
  );
}

ZipIntakeStep.propTypes = {
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
function ColumnMapper({ table, header, setHeader, roles, setRoles }) {
  const preview = (header ? table.slice(1) : table).slice(0, 5);
  const width = roles.length;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Which column is which?</Typography>
        <Chip
          size="small"
          variant={header ? 'filled' : 'outlined'}
          label={header ? 'First row is a header' : 'No header row'}
          onClick={() => setHeader(!header)}
        />
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            {Array.from({ length: width }, (_, i) => (
              <TableCell key={i} sx={{ minWidth: 140 }}>
                <Select
                  size="small"
                  fullWidth
                  value={roles[i]}
                  onChange={(e) => {
                    const next = [...roles];
                    // Only one ZIP / territory / vertical / brand column.
                    if (e.target.value !== 'ignore') {
                      const prev = next.indexOf(e.target.value);
                      if (prev >= 0) next[prev] = 'ignore';
                    }
                    next[i] = e.target.value;
                    setRoles(next);
                  }}
                >
                  {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                </Select>
                {header && (
                  <Typography variant="caption" color="text.secondary">{table[0][i]}</Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {preview.map((r, ri) => (
            <TableRow key={ri}>
              {Array.from({ length: width }, (_, i) => (
                <TableCell key={i} sx={{ color: roles[i] === 'ignore' ? 'text.disabled' : 'inherit' }}>{r[i] || ''}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary">
        {fmt((header ? table.length - 1 : table.length))} rows. Leave columns you don't need as Ignore — city, county and state columns aren't needed.
      </Typography>
    </Box>
  );
}

ColumnMapper.propTypes = {
  table: PropTypes.array.isRequired,
  header: PropTypes.bool.isRequired,
  setHeader: PropTypes.func.isRequired,
  roles: PropTypes.array.isRequired,
  setRoles: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
const RADII = [0, 10, 25, 50, 100];

function AreaPicker({ areas, setAreas }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [radius, setRadius] = useState(0);

  useEffect(() => {
    if (query.trim().length < 2) { setOptions([]); return undefined; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchProspectServiceAreas(query.trim());
        setOptions(res?.data?.results || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const addArea = async (area) => {
    if (!area) return;
    const r = area.kind === 'city' ? radius : 0;
    const label = r ? `${area.label} (+${r} mi)` : area.label;
    if (areas.some((a) => a.label === label)) return;
    setAdding(true);
    try {
      const res = await expandProspectServiceAreas([{ ...area, radius_miles: r, label: area.name }]);
      setAreas([...areas, { ...area, label, radius_miles: r, rows: res?.data?.rows || [] }]);
      setQuery('');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Autocomplete
          sx={{ flex: 1 }}
          options={options}
          loading={loading}
          filterOptions={(x) => x}
          getOptionLabel={(o) => o.label || ''}
          inputValue={query}
          onInputChange={(_, v, reason) => { if (reason !== 'reset') setQuery(v); }}
          onChange={(_, v) => addArea(v)}
          value={null}
          isOptionEqualToValue={(a, b) => a.label === b.label && a.kind === b.kind}
          renderOption={(props, o) => (
            <li {...props} key={`${o.kind}-${o.label}`}>
              <Stack>
                <Typography variant="body2">{o.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.kind === 'state' ? 'Whole state' : o.kind === 'county' ? 'County' : 'City'} · {fmt(o.zip_count)} ZIPs · {fmtPop(o.population)} people
                </Typography>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Search a county, city or state" placeholder="e.g. Bucks County PA, Hickory NC, New Jersey" />
          )}
        />
        <TextField
          select
          label="Around a city"
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
          sx={{ minWidth: 150 }}
          helperText="Cities only"
        >
          {RADII.map((r) => <MenuItem key={r} value={r}>{r ? `+ ${r} miles` : 'City only'}</MenuItem>)}
        </TextField>
      </Stack>
      {adding && <CircularProgress size={18} />}
      {areas.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {areas.map((a) => (
            <Chip
              key={a.label}
              label={`${a.label} · ${fmt(a.rows.length)} ZIPs`}
              onDelete={() => setAreas(areas.filter((x) => x.label !== a.label))}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

AreaPicker.propTypes = {
  areas: PropTypes.array.isRequired,
  setAreas: PropTypes.func.isRequired,
};
